import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type Country = "us" | "br" | "es" | "fr" | "de" | "it";
export type Language = "en" | "pt" | "es" | "fr" | "de" | "it";

interface LocaleConfig {
  country: Country;
  language: Language;
  currency: string;
  currencySymbol: string;
  flag: string;
  name: string;
  shortName: string;
}

const localeConfigs: Record<Country, LocaleConfig> = {
  us: {
    country: "us",
    language: "en",
    currency: "USD",
    currencySymbol: "$",
    flag: "🇺🇸",
    name: "English",
    shortName: "EN",
  },
  br: {
    country: "br",
    language: "pt",
    currency: "BRL",
    currencySymbol: "R$",
    flag: "🇧🇷",
    name: "Português",
    shortName: "PT",
  },
  es: {
    country: "es",
    language: "es",
    currency: "EUR",
    currencySymbol: "€",
    flag: "🇪🇸",
    name: "Español",
    shortName: "ES",
  },
  fr: {
    country: "fr",
    language: "fr",
    currency: "EUR",
    currencySymbol: "€",
    flag: "🇫🇷",
    name: "Français",
    shortName: "FR",
  },
  de: {
    country: "de",
    language: "de",
    currency: "EUR",
    currencySymbol: "€",
    flag: "🇩🇪",
    name: "Deutsch",
    shortName: "DE",
  },
  it: {
    country: "it",
    language: "it",
    currency: "EUR",
    currencySymbol: "€",
    flag: "🇮🇹",
    name: "Italiano",
    shortName: "IT",
  },
};

interface LocaleContextType {
  locale: LocaleConfig;
  setCountry: (country: Country) => void;
  t: (key: string) => string;
  formatCurrency: (amount: number) => string;
  availableCountries: LocaleConfig[];
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Translations
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navbar
    "nav.events": "Events",
    "nav.howItWorks": "How it Works",
    "nav.leaderboard": "Leaderboard",
    "nav.login": "Log In",
    "nav.getStarted": "Get Started",
    // Hero
    "hero.badge": "Predict. Compete. Win.",
    "hero.title1": "Forecast events online!",
    "hero.title2": "Earn coins & climb leaderboards.",
    "hero.description": "Join the future of event predictions. Make predictions on upcoming events, compete with others, and earn CallyCoins for accurate predictions.",
    "hero.cta1": "Start Predicting",
    "hero.ctaBonus": "🎁 Get 100 CallyCoins FREE",
    "hero.cta2": "Browse Events",
    "hero.stat1": "Active Predictors",
    "hero.stat2": "Total Volume",
    "hero.stat3": "Live Events",
    // Events Section
    "events.badge": "Live Markets",
    "events.title": "Trending Events",
    "events.description": "Browse the hottest prediction markets. Make your predictions and compete with thousands of predictors worldwide.",
    "events.all": "All",
    "events.sports": "Sports",
    "events.crypto": "Crypto",
    "events.politics": "Politics",
    "events.tech": "Tech",
    "events.entertainment": "Entertainment",
    "events.viewAll": "View All Events",
    "events.pool": "pool",
    "events.bids": "predictions",
    "events.bidYes": "YES",
    "events.bidNo": "NO",
    "events.ended": "Ended",
    "events.remaining": "remaining",
    "events.allCountries": "All Countries",
    "events.searchPlaceholder": "Search events...",
    "events.statusAll": "All",
    "events.statusLive": "Live",
    "events.statusEnded": "Ended",
    "events.statusResolved": "Resolved",
    "events.noResults": "No events found matching your search.",
    // How it Works
    "how.badge": "Simple Process",
    "how.title": "How Callyout Works",
    "how.description": "Start predicting in minutes. Our platform makes it easy to participate in event prediction markets.",
    "how.step1.title": "Create Account",
    "how.step1.description": "Sign up for free and get your starting CallyCoins balance to begin predicting.",
    "how.step2.title": "Browse Events",
    "how.step2.description": "Explore upcoming events across sports, crypto, politics, tech, and entertainment.",
    "how.step3.title": "Make Your Predictions",
    "how.step3.description": "Choose YES or NO on event outcomes. The more accurate your predictions, the more you win.",
    "how.step4.title": "Earn Rewards",
    "how.step4.description": "Win CallyCoins from the pool when your predictions are correct. Climb the leaderboard!",
    "how.step5.title": "Create Events & Earn",
    "how.step5.description": "Create your own events and earn 2% of the losing pool when the event is resolved. Be the house!",
    // Footer
    "footer.tagline": "The future of event predictions",
    "footer.platform": "Platform",
    "footer.company": "Company",
    "footer.legal": "Legal",
    "footer.rights": "All rights reserved.",
    // Store
    "store.title": "Get",
    "store.titleHighlight": "CallyCoins",
    "store.description": "Purchase coins to make predictions or subscribe for monthly rewards",
    "store.activeSubscription": "Active Subscription",
    "store.renews": "Renews",
    "store.manageSubscription": "Manage Subscription",
    "store.coinPackages": "Coin Packages",
    "store.mostPopular": "Most Popular",
    "store.save": "Save 20%",
    "store.bestValue": "Best Value!",
    "store.oneTimePurchase": "One-time purchase",
    "store.buyNow": "Buy Now",
    "store.monthlySubscriptions": "Monthly Subscriptions",
    "store.recommended": "Recommended",
    "store.yourPlan": "Your Plan",
    "store.perMonth": "/month",
    "store.coinsEveryMonth": "coins every month",
    "store.managePlan": "Manage Plan",
    "store.subscribe": "Subscribe",
    "store.basicMonthly": "Basic Monthly",
    "store.premiumMonthly": "Premium Monthly",
    "store.coinsPerMonth": "CallyCoins/month",
    "store.standardSupport": "Standard support",
    "store.prioritySupport": "Priority support",
    "store.earlyAccess": "Early access to events",
    // Promo Page
    "promo.urgencyBadge": "🎁 Limited Time: Free Sign-up Bonus",
    "promo.headline": "Turn Predictions Into Rewards",
    "promo.subheadline": "Join thousands making predictions on real-world events and earning CallyCoins",
    "promo.ctaPrimary": "Claim Your 100 FREE CallyCoins",
    "promo.ctaSubtext": "No credit card required",
    "promo.noCreditCard": "✓ No credit card • ✓ Instant access",
    "promo.statPredictors": "Predictors",
    "promo.statMarkets": "Live Markets",
    "promo.statWagered": "CallyCoins Wagered",
    "promo.benefitsTitle": "Why Callyout?",
    "promo.benefit1Title": "Free to Start",
    "promo.benefit1Desc": "Get 100 CallyCoins instantly, no credit card needed",
    "promo.benefit2Title": "Win Real Rewards",
    "promo.benefit2Desc": "93% of the losing pool goes to winners",
    "promo.benefit3Title": "Create & Earn",
    "promo.benefit3Desc": "Make events and earn 2% creator fees",
    "promo.howItWorksTitle": "How It Works",
    "promo.step1Title": "Pick an Event",
    "promo.step1Desc": "Browse trending prediction markets",
    "promo.step2Title": "Make Your Call",
    "promo.step2Desc": "Predict YES or NO on outcomes",
    "promo.step3Title": "Win CallyCoins",
    "promo.step3Desc": "Earn when you're right",
    "promo.trustSecure": "Secure",
    "promo.trustInstant": "Instant",
    "promo.trustFair": "Fair",
    "promo.trust247": "24/7",
    "promo.finalCtaTitle": "Ready to Start Predicting?",
    "promo.finalCtaSubtitle": "Join our growing community of predictors today",
    "promo.finalCtaButton": "Get Started Free",
    "promo.terms": "Terms",
    "promo.privacy": "Privacy",
    "promo.startPredicting": "Start Predicting",
    "promo.signUpFirst": "Sign up first to claim",
  },
  pt: {
    // Navbar
    "nav.events": "Eventos",
    "nav.howItWorks": "Como Funciona",
    "nav.leaderboard": "Ranking",
    "nav.login": "Entrar",
    "nav.getStarted": "Começar",
    // Hero
    "hero.badge": "Preveja. Compita. Ganhe.",
    "hero.title1": "Preveja eventos online!",
    "hero.title2": "Ganhe moedas e suba no ranking.",
    "hero.description": "Junte-se ao futuro das previsões de eventos. Faça previsões em eventos, compita com outros e ganhe CallyCoins por previsões precisas.",
    "hero.cta1": "Começar a Prever",
    "hero.ctaBonus": "🎁 Ganhe 100 CallyCoins GRÁTIS",
    "hero.cta2": "Ver Eventos",
    "hero.stat1": "Previsores Ativos",
    "hero.stat2": "Volume Total",
    "hero.stat3": "Eventos ao Vivo",
    // Events Section
    "events.badge": "Mercados ao Vivo",
    "events.title": "Eventos em Alta",
    "events.description": "Explore os mercados de previsão mais populares. Faça suas previsões e compita com milhares de previsores no mundo todo.",
    "events.all": "Todos",
    "events.sports": "Esportes",
    "events.crypto": "Cripto",
    "events.politics": "Política",
    "events.tech": "Tech",
    "events.entertainment": "Entretenimento",
    "events.viewAll": "Ver Todos os Eventos",
    "events.pool": "pool",
    "events.bids": "previsões",
    "events.bidYes": "SIM",
    "events.bidNo": "NÃO",
    "events.ended": "Encerrado",
    "events.remaining": "restante",
    "events.allCountries": "Todos os Países",
    "events.searchPlaceholder": "Buscar eventos...",
    "events.statusAll": "Todos",
    "events.statusLive": "Ao Vivo",
    "events.statusEnded": "Encerrados",
    "events.statusResolved": "Resolvidos",
    "events.noResults": "Nenhum evento encontrado.",
    // How it Works
    "how.badge": "Processo Simples",
    "how.title": "Como o Callyout Funciona",
    "how.description": "Comece a prever em minutos. Nossa plataforma facilita a participação nos mercados de previsão.",
    "how.step1.title": "Criar Conta",
    "how.step1.description": "Cadastre-se gratuitamente e receba seu saldo inicial de CallyCoins para começar a prever.",
    "how.step2.title": "Explorar Eventos",
    "how.step2.description": "Explore eventos em esportes, cripto, política, tecnologia e entretenimento.",
    "how.step3.title": "Fazer Previsões",
    "how.step3.description": "Escolha SIM ou NÃO nos resultados dos eventos. Quanto mais precisas suas previsões, mais você ganha.",
    "how.step4.title": "Ganhar Recompensas",
    "how.step4.description": "Ganhe CallyCoins do pool quando suas previsões estiverem corretas. Suba no ranking!",
    "how.step5.title": "Crie Eventos & Ganhe",
    "how.step5.description": "Crie seus próprios eventos e ganhe 2% do pool dos perdedores quando o evento for resolvido. Seja a casa!",
    // Footer
    "footer.tagline": "O futuro das previsões de eventos",
    "footer.platform": "Plataforma",
    "footer.company": "Empresa",
    "footer.legal": "Legal",
    "footer.rights": "Todos os direitos reservados.",
    // Store
    "store.title": "Obter",
    "store.titleHighlight": "CallyCoins",
    "store.description": "Compre moedas para fazer previsões ou assine para recompensas mensais",
    "store.activeSubscription": "Assinatura Ativa",
    "store.renews": "Renova em",
    "store.manageSubscription": "Gerenciar Assinatura",
    "store.coinPackages": "Pacotes de Moedas",
    "store.mostPopular": "Mais Popular",
    "store.save": "Economize 20%",
    "store.bestValue": "Melhor Valor!",
    "store.oneTimePurchase": "Compra única",
    "store.buyNow": "Comprar",
    "store.monthlySubscriptions": "Assinaturas Mensais",
    "store.recommended": "Recomendado",
    "store.yourPlan": "Seu Plano",
    "store.perMonth": "/mês",
    "store.coinsEveryMonth": "moedas todo mês",
    "store.managePlan": "Gerenciar Plano",
    "store.subscribe": "Assinar",
    "store.basicMonthly": "Básico Mensal",
    "store.premiumMonthly": "Premium Mensal",
    "store.coinsPerMonth": "CallyCoins/mês",
    "store.standardSupport": "Suporte padrão",
    "store.prioritySupport": "Suporte prioritário",
    "store.earlyAccess": "Acesso antecipado a eventos",
    // Promo Page
    "promo.urgencyBadge": "🎁 Tempo Limitado: Bônus de Cadastro Grátis",
    "promo.headline": "Transforme Previsões em Recompensas",
    "promo.subheadline": "Junte-se a milhares fazendo previsões em eventos reais e ganhando CallyCoins",
    "promo.ctaPrimary": "Resgate Seus 100 CallyCoins GRÁTIS",
    "promo.ctaSubtext": "Sem cartão de crédito",
    "promo.noCreditCard": "✓ Sem cartão • ✓ Acesso instantâneo",
    "promo.statPredictors": "Previsores",
    "promo.statMarkets": "Mercados ao Vivo",
    "promo.statWagered": "CallyCoins Apostados",
    "promo.benefitsTitle": "Por que Callyout?",
    "promo.benefit1Title": "Grátis para Começar",
    "promo.benefit1Desc": "Ganhe 100 CallyCoins instantaneamente, sem cartão",
    "promo.benefit2Title": "Ganhe Recompensas Reais",
    "promo.benefit2Desc": "93% do pool perdedor vai para os vencedores",
    "promo.benefit3Title": "Crie e Ganhe",
    "promo.benefit3Desc": "Crie eventos e ganhe 2% de taxa de criador",
    "promo.howItWorksTitle": "Como Funciona",
    "promo.step1Title": "Escolha um Evento",
    "promo.step1Desc": "Navegue pelos mercados de previsão populares",
    "promo.step2Title": "Faça Sua Previsão",
    "promo.step2Desc": "Preveja SIM ou NÃO nos resultados",
    "promo.step3Title": "Ganhe CallyCoins",
    "promo.step3Desc": "Ganhe quando você acertar",
    "promo.trustSecure": "Seguro",
    "promo.trustInstant": "Instantâneo",
    "promo.trustFair": "Justo",
    "promo.trust247": "24/7",
    "promo.finalCtaTitle": "Pronto para Começar a Prever?",
    "promo.finalCtaSubtitle": "Junte-se à nossa comunidade crescente de previsores",
    "promo.finalCtaButton": "Comece Grátis",
    "promo.terms": "Termos",
    "promo.privacy": "Privacidade",
    "promo.startPredicting": "Começar a Prever",
    "promo.signUpFirst": "Cadastre-se primeiro",
  },
  es: {
    // Navbar
    "nav.events": "Eventos",
    "nav.howItWorks": "Cómo Funciona",
    "nav.leaderboard": "Clasificación",
    "nav.login": "Iniciar Sesión",
    "nav.getStarted": "Comenzar",
    // Hero
    "hero.badge": "Predice. Compite. Gana.",
    "hero.title1": "¡Pronostica eventos en línea!",
    "hero.title2": "Gana monedas y sube en la clasificación.",
    "hero.description": "Únete al futuro de las predicciones de eventos. Haz predicciones en próximos eventos, compite con otros y gana CallyCoins por predicciones precisas.",
    "hero.cta1": "Empezar a Predecir",
    "hero.ctaBonus": "🎁 Obtén 100 CallyCoins GRATIS",
    "hero.cta2": "Ver Eventos",
    "hero.stat1": "Predictores Activos",
    "hero.stat2": "Volumen Total",
    "hero.stat3": "Eventos en Vivo",
    // Events Section
    "events.badge": "Mercados en Vivo",
    "events.title": "Eventos Populares",
    "events.description": "Explora los mercados de predicción más populares. Haz tus predicciones y compite con miles de predictores en todo el mundo.",
    "events.all": "Todos",
    "events.sports": "Deportes",
    "events.crypto": "Cripto",
    "events.politics": "Política",
    "events.tech": "Tech",
    "events.entertainment": "Entretenimiento",
    "events.viewAll": "Ver Todos los Eventos",
    "events.pool": "pool",
    "events.bids": "predicciones",
    "events.bidYes": "SÍ",
    "events.bidNo": "NO",
    "events.ended": "Terminado",
    "events.remaining": "restante",
    "events.allCountries": "Todos los Países",
    "events.searchPlaceholder": "Buscar eventos...",
    "events.statusAll": "Todos",
    "events.statusLive": "En Vivo",
    "events.statusEnded": "Terminados",
    "events.statusResolved": "Resueltos",
    "events.noResults": "No se encontraron eventos.",
    // How it Works
    "how.badge": "Proceso Simple",
    "how.title": "Cómo Funciona Callyout",
    "how.description": "Comienza a predecir en minutos. Nuestra plataforma facilita la participación en mercados de predicción.",
    "how.step1.title": "Crear Cuenta",
    "how.step1.description": "Regístrate gratis y obtén tu saldo inicial de CallyCoins para comenzar a predecir.",
    "how.step2.title": "Explorar Eventos",
    "how.step2.description": "Explora eventos en deportes, cripto, política, tecnología y entretenimiento.",
    "how.step3.title": "Hacer Predicciones",
    "how.step3.description": "Elige SÍ o NO en los resultados de eventos. Cuanto más precisas sean tus predicciones, más ganas.",
    "how.step4.title": "Ganar Recompensas",
    "how.step4.description": "Gana CallyCoins del pool cuando tus predicciones sean correctas. ¡Sube en la clasificación!",
    "how.step5.title": "Crea Eventos y Gana",
    "how.step5.description": "Crea tus propios eventos y gana el 2% del pool perdedor cuando se resuelva el evento. ¡Sé la casa!",
    // Footer
    "footer.tagline": "El futuro de las predicciones de eventos",
    "footer.platform": "Plataforma",
    "footer.company": "Empresa",
    "footer.legal": "Legal",
    "footer.rights": "Todos los derechos reservados.",
    // Store
    "store.title": "Obtener",
    "store.titleHighlight": "CallyCoins",
    "store.description": "Compra monedas para hacer predicciones o suscríbete para recompensas mensuales",
    "store.activeSubscription": "Suscripción Activa",
    "store.renews": "Se renueva",
    "store.manageSubscription": "Gestionar Suscripción",
    "store.coinPackages": "Paquetes de Monedas",
    "store.mostPopular": "Más Popular",
    "store.save": "Ahorra 20%",
    "store.bestValue": "¡Mejor Valor!",
    "store.oneTimePurchase": "Compra única",
    "store.buyNow": "Comprar Ahora",
    "store.monthlySubscriptions": "Suscripciones Mensuales",
    "store.recommended": "Recomendado",
    "store.yourPlan": "Tu Plan",
    "store.perMonth": "/mes",
    "store.coinsEveryMonth": "monedas cada mes",
    "store.managePlan": "Gestionar Plan",
    "store.subscribe": "Suscribirse",
    "store.basicMonthly": "Básico Mensual",
    "store.premiumMonthly": "Premium Mensual",
    "store.coinsPerMonth": "CallyCoins/mes",
    "store.standardSupport": "Soporte estándar",
    "store.prioritySupport": "Soporte prioritario",
    "store.earlyAccess": "Acceso anticipado a eventos",
    // Promo Page
    "promo.urgencyBadge": "🎁 Tiempo Limitado: Bono de Registro Gratis",
    "promo.headline": "Convierte Predicciones en Recompensas",
    "promo.subheadline": "Únete a miles haciendo predicciones en eventos reales y ganando CallyCoins",
    "promo.ctaPrimary": "Reclama Tus 100 CallyCoins GRATIS",
    "promo.ctaSubtext": "Sin tarjeta de crédito",
    "promo.noCreditCard": "✓ Sin tarjeta • ✓ Acceso instantáneo",
    "promo.statPredictors": "Predictores",
    "promo.statMarkets": "Mercados en Vivo",
    "promo.statWagered": "CallyCoins Apostados",
    "promo.benefitsTitle": "¿Por qué Callyout?",
    "promo.benefit1Title": "Gratis para Empezar",
    "promo.benefit1Desc": "Obtén 100 CallyCoins al instante, sin tarjeta",
    "promo.benefit2Title": "Gana Recompensas Reales",
    "promo.benefit2Desc": "93% del pool perdedor va a los ganadores",
    "promo.benefit3Title": "Crea y Gana",
    "promo.benefit3Desc": "Crea eventos y gana 2% de comisión de creador",
    "promo.howItWorksTitle": "Cómo Funciona",
    "promo.step1Title": "Elige un Evento",
    "promo.step1Desc": "Explora los mercados de predicción populares",
    "promo.step2Title": "Haz Tu Predicción",
    "promo.step2Desc": "Predice SÍ o NO en los resultados",
    "promo.step3Title": "Gana CallyCoins",
    "promo.step3Desc": "Gana cuando aciertes",
    "promo.trustSecure": "Seguro",
    "promo.trustInstant": "Instantáneo",
    "promo.trustFair": "Justo",
    "promo.trust247": "24/7",
    "promo.finalCtaTitle": "¿Listo para Empezar a Predecir?",
    "promo.finalCtaSubtitle": "Únete a nuestra comunidad creciente de predictores",
    "promo.finalCtaButton": "Comienza Gratis",
    "promo.terms": "Términos",
    "promo.privacy": "Privacidad",
    "promo.startPredicting": "Empezar a Predecir",
    "promo.signUpFirst": "Regístrate primero",
  },
  fr: {
    // Navbar
    "nav.events": "Événements",
    "nav.howItWorks": "Comment ça marche",
    "nav.leaderboard": "Classement",
    "nav.login": "Connexion",
    "nav.getStarted": "Commencer",
    // Hero
    "hero.badge": "Prédisez. Rivalisez. Gagnez.",
    "hero.title1": "Pronostiquez des événements en ligne !",
    "hero.title2": "Gagnez des pièces et grimpez au classement.",
    "hero.description": "Rejoignez l'avenir des prédictions d'événements. Faites des prédictions, rivalisez avec d'autres et gagnez des CallyCoins.",
    "hero.cta1": "Commencer à prédire",
    "hero.ctaBonus": "🎁 Obtenez 100 CallyCoins GRATUITS",
    "hero.cta2": "Parcourir les événements",
    "hero.stat1": "Prédicteurs actifs",
    "hero.stat2": "Volume total",
    "hero.stat3": "Événements en direct",
    // Events Section
    "events.badge": "Marchés en direct",
    "events.title": "Événements tendance",
    "events.description": "Parcourez les marchés de prédiction les plus populaires.",
    "events.all": "Tous",
    "events.sports": "Sports",
    "events.crypto": "Crypto",
    "events.politics": "Politique",
    "events.tech": "Tech",
    "events.entertainment": "Divertissement",
    "events.viewAll": "Voir tous les événements",
    "events.pool": "cagnotte",
    "events.bids": "prédictions",
    "events.bidYes": "OUI",
    "events.bidNo": "NON",
    "events.ended": "Terminé",
    "events.remaining": "restant",
    "events.allCountries": "Tous les pays",
    "events.searchPlaceholder": "Rechercher des événements...",
    "events.statusAll": "Tous",
    "events.statusLive": "En direct",
    "events.statusEnded": "Terminés",
    "events.statusResolved": "Résolus",
    "events.noResults": "Aucun événement trouvé.",
    // How it Works
    "how.badge": "Processus simple",
    "how.title": "Comment fonctionne Callyout",
    "how.description": "Commencez à prédire en quelques minutes.",
    "how.step1.title": "Créer un compte",
    "how.step1.description": "Inscrivez-vous gratuitement et obtenez vos CallyCoins de départ.",
    "how.step2.title": "Parcourir les événements",
    "how.step2.description": "Explorez les événements dans divers domaines.",
    "how.step3.title": "Faire des prédictions",
    "how.step3.description": "Choisissez OUI ou NON sur les résultats des événements.",
    "how.step4.title": "Gagner des récompenses",
    "how.step4.description": "Gagnez des CallyCoins lorsque vos prédictions sont correctes.",
    "how.step5.title": "Créer des événements et gagner",
    "how.step5.description": "Créez vos propres événements et gagnez 2% de la cagnotte des perdants.",
    // Footer
    "footer.tagline": "L'avenir des prédictions d'événements",
    "footer.platform": "Plateforme",
    "footer.company": "Entreprise",
    "footer.legal": "Légal",
    "footer.rights": "Tous droits réservés.",
    // Store
    "store.title": "Obtenir",
    "store.titleHighlight": "CallyCoins",
    "store.description": "Achetez des pièces pour faire des prédictions",
    "store.activeSubscription": "Abonnement actif",
    "store.renews": "Se renouvelle",
    "store.manageSubscription": "Gérer l'abonnement",
    "store.coinPackages": "Paquets de pièces",
    "store.mostPopular": "Plus populaire",
    "store.save": "Économisez 20%",
    "store.bestValue": "Meilleur rapport qualité-prix !",
    "store.oneTimePurchase": "Achat unique",
    "store.buyNow": "Acheter",
    "store.monthlySubscriptions": "Abonnements mensuels",
    "store.recommended": "Recommandé",
    "store.yourPlan": "Votre forfait",
    "store.perMonth": "/mois",
    "store.coinsEveryMonth": "pièces chaque mois",
    "store.managePlan": "Gérer le forfait",
    "store.subscribe": "S'abonner",
    "store.basicMonthly": "Basique mensuel",
    "store.premiumMonthly": "Premium mensuel",
    "store.coinsPerMonth": "CallyCoins/mois",
    "store.standardSupport": "Support standard",
    "store.prioritySupport": "Support prioritaire",
    "store.earlyAccess": "Accès anticipé aux événements",
    // Promo Page
    "promo.urgencyBadge": "🎁 Temps Limité: Bonus d'Inscription Gratuit",
    "promo.headline": "Transformez vos Prédictions en Récompenses",
    "promo.subheadline": "Rejoignez des milliers de personnes faisant des prédictions sur des événements réels et gagnant des CallyCoins",
    "promo.ctaPrimary": "Réclamez Vos 100 CallyCoins GRATUITS",
    "promo.ctaSubtext": "Sans carte de crédit",
    "promo.noCreditCard": "✓ Sans carte • ✓ Accès instantané",
    "promo.statPredictors": "Prédicteurs",
    "promo.statMarkets": "Marchés en Direct",
    "promo.statWagered": "CallyCoins Misés",
    "promo.benefitsTitle": "Pourquoi Callyout?",
    "promo.benefit1Title": "Gratuit pour Commencer",
    "promo.benefit1Desc": "Obtenez 100 CallyCoins instantanément, sans carte",
    "promo.benefit2Title": "Gagnez de Vraies Récompenses",
    "promo.benefit2Desc": "93% du pool perdant va aux gagnants",
    "promo.benefit3Title": "Créez et Gagnez",
    "promo.benefit3Desc": "Créez des événements et gagnez 2% de frais de créateur",
    "promo.howItWorksTitle": "Comment ça Marche",
    "promo.step1Title": "Choisissez un Événement",
    "promo.step1Desc": "Parcourez les marchés de prédiction populaires",
    "promo.step2Title": "Faites Votre Prédiction",
    "promo.step2Desc": "Prédisez OUI ou NON sur les résultats",
    "promo.step3Title": "Gagnez des CallyCoins",
    "promo.step3Desc": "Gagnez quand vous avez raison",
    "promo.trustSecure": "Sécurisé",
    "promo.trustInstant": "Instantané",
    "promo.trustFair": "Équitable",
    "promo.trust247": "24/7",
    "promo.finalCtaTitle": "Prêt à Commencer à Prédire?",
    "promo.finalCtaSubtitle": "Rejoignez notre communauté croissante de prédicteurs",
    "promo.finalCtaButton": "Commencez Gratuitement",
    "promo.terms": "Conditions",
    "promo.privacy": "Confidentialité",
    "promo.startPredicting": "Commencer à Prédire",
    "promo.signUpFirst": "Inscrivez-vous d'abord",
  },
  de: {
    // Navbar
    "nav.events": "Veranstaltungen",
    "nav.howItWorks": "So funktioniert's",
    "nav.leaderboard": "Bestenliste",
    "nav.login": "Anmelden",
    "nav.getStarted": "Loslegen",
    // Hero
    "hero.badge": "Vorhersagen. Wettbewerb. Gewinnen.",
    "hero.title1": "Events online vorhersagen!",
    "hero.title2": "Münzen verdienen & Bestenlisten erklimmen.",
    "hero.description": "Treten Sie der Zukunft der Ereignisvorhersagen bei. Machen Sie Vorhersagen und gewinnen Sie CallyCoins.",
    "hero.cta1": "Vorhersagen starten",
    "hero.ctaBonus": "🎁 Erhalten Sie 100 CallyCoins GRATIS",
    "hero.cta2": "Events durchsuchen",
    "hero.stat1": "Aktive Vorhersager",
    "hero.stat2": "Gesamtvolumen",
    "hero.stat3": "Live-Events",
    // Events Section
    "events.badge": "Live-Märkte",
    "events.title": "Trend-Events",
    "events.description": "Durchsuchen Sie die beliebtesten Vorhersagemärkte.",
    "events.all": "Alle",
    "events.sports": "Sport",
    "events.crypto": "Krypto",
    "events.politics": "Politik",
    "events.tech": "Tech",
    "events.entertainment": "Unterhaltung",
    "events.viewAll": "Alle Events anzeigen",
    "events.pool": "Pool",
    "events.bids": "Vorhersagen",
    "events.bidYes": "JA",
    "events.bidNo": "NEIN",
    "events.ended": "Beendet",
    "events.remaining": "verbleibend",
    "events.allCountries": "Alle Länder",
    "events.searchPlaceholder": "Events suchen...",
    "events.statusAll": "Alle",
    "events.statusLive": "Live",
    "events.statusEnded": "Beendet",
    "events.statusResolved": "Gelöst",
    "events.noResults": "Keine Events gefunden.",
    // How it Works
    "how.badge": "Einfacher Prozess",
    "how.title": "So funktioniert Callyout",
    "how.description": "Beginnen Sie in wenigen Minuten mit Vorhersagen.",
    "how.step1.title": "Konto erstellen",
    "how.step1.description": "Registrieren Sie sich kostenlos und erhalten Sie Ihr Start-CallyCoins-Guthaben.",
    "how.step2.title": "Events durchsuchen",
    "how.step2.description": "Erkunden Sie Events in verschiedenen Kategorien.",
    "how.step3.title": "Vorhersagen machen",
    "how.step3.description": "Wählen Sie JA oder NEIN bei Event-Ergebnissen.",
    "how.step4.title": "Belohnungen verdienen",
    "how.step4.description": "Gewinnen Sie CallyCoins, wenn Ihre Vorhersagen richtig sind.",
    "how.step5.title": "Events erstellen & verdienen",
    "how.step5.description": "Erstellen Sie eigene Events und verdienen Sie 2% des Verliererpools.",
    // Footer
    "footer.tagline": "Die Zukunft der Ereignisvorhersagen",
    "footer.platform": "Plattform",
    "footer.company": "Unternehmen",
    "footer.legal": "Rechtliches",
    "footer.rights": "Alle Rechte vorbehalten.",
    // Store
    "store.title": "Erhalten",
    "store.titleHighlight": "CallyCoins",
    "store.description": "Kaufen Sie Münzen für Vorhersagen",
    "store.activeSubscription": "Aktives Abonnement",
    "store.renews": "Verlängert sich",
    "store.manageSubscription": "Abonnement verwalten",
    "store.coinPackages": "Münzpakete",
    "store.mostPopular": "Am beliebtesten",
    "store.save": "20% sparen",
    "store.bestValue": "Bester Wert!",
    "store.oneTimePurchase": "Einmaliger Kauf",
    "store.buyNow": "Jetzt kaufen",
    "store.monthlySubscriptions": "Monatsabonnements",
    "store.recommended": "Empfohlen",
    "store.yourPlan": "Ihr Plan",
    "store.perMonth": "/Monat",
    "store.coinsEveryMonth": "Münzen jeden Monat",
    "store.managePlan": "Plan verwalten",
    "store.subscribe": "Abonnieren",
    "store.basicMonthly": "Basis monatlich",
    "store.premiumMonthly": "Premium monatlich",
    "store.coinsPerMonth": "CallyCoins/Monat",
    "store.standardSupport": "Standard-Support",
    "store.prioritySupport": "Prioritäts-Support",
    "store.earlyAccess": "Frühzeitiger Zugang zu Events",
    // Promo Page
    "promo.urgencyBadge": "🎁 Begrenzte Zeit: Kostenloser Anmeldebonus",
    "promo.headline": "Verwandle Vorhersagen in Belohnungen",
    "promo.subheadline": "Schließe dich Tausenden an, die Vorhersagen zu echten Ereignissen treffen und CallyCoins verdienen",
    "promo.ctaPrimary": "Fordere Deine 100 GRATIS CallyCoins",
    "promo.ctaSubtext": "Keine Kreditkarte erforderlich",
    "promo.noCreditCard": "✓ Keine Karte • ✓ Sofortiger Zugang",
    "promo.statPredictors": "Vorhersager",
    "promo.statMarkets": "Live-Märkte",
    "promo.statWagered": "CallyCoins Gesetzt",
    "promo.benefitsTitle": "Warum Callyout?",
    "promo.benefit1Title": "Kostenlos Starten",
    "promo.benefit1Desc": "Erhalte 100 CallyCoins sofort, keine Karte nötig",
    "promo.benefit2Title": "Echte Belohnungen Gewinnen",
    "promo.benefit2Desc": "93% des Verlustpools geht an die Gewinner",
    "promo.benefit3Title": "Erstellen und Verdienen",
    "promo.benefit3Desc": "Erstelle Events und verdiene 2% Erstellergebühr",
    "promo.howItWorksTitle": "So Funktioniert's",
    "promo.step1Title": "Wähle ein Event",
    "promo.step1Desc": "Durchsuche beliebte Vorhersagemärkte",
    "promo.step2Title": "Triff Deine Vorhersage",
    "promo.step2Desc": "Sage JA oder NEIN zu Ergebnissen voraus",
    "promo.step3Title": "Gewinne CallyCoins",
    "promo.step3Desc": "Verdiene, wenn du richtig liegst",
    "promo.trustSecure": "Sicher",
    "promo.trustInstant": "Sofort",
    "promo.trustFair": "Fair",
    "promo.trust247": "24/7",
    "promo.finalCtaTitle": "Bereit zu Starten?",
    "promo.finalCtaSubtitle": "Werde Teil unserer wachsenden Vorhersager-Community",
    "promo.finalCtaButton": "Kostenlos Starten",
    "promo.terms": "AGB",
    "promo.privacy": "Datenschutz",
    "promo.startPredicting": "Vorhersagen Starten",
    "promo.signUpFirst": "Erst registrieren",
  },
  it: {
    // Navbar
    "nav.events": "Eventi",
    "nav.howItWorks": "Come funziona",
    "nav.leaderboard": "Classifica",
    "nav.login": "Accedi",
    "nav.getStarted": "Inizia",
    // Hero
    "hero.badge": "Prevedi. Competi. Vinci.",
    "hero.title1": "Prevedi eventi online!",
    "hero.title2": "Guadagna monete e scala le classifiche.",
    "hero.description": "Unisciti al futuro delle previsioni di eventi. Fai previsioni e guadagna CallyCoins.",
    "hero.cta1": "Inizia a prevedere",
    "hero.ctaBonus": "🎁 Ottieni 100 CallyCoins GRATIS",
    "hero.cta2": "Sfoglia eventi",
    "hero.stat1": "Predittori attivi",
    "hero.stat2": "Volume totale",
    "hero.stat3": "Eventi in diretta",
    // Events Section
    "events.badge": "Mercati in diretta",
    "events.title": "Eventi di tendenza",
    "events.description": "Sfoglia i mercati di previsione più popolari.",
    "events.all": "Tutti",
    "events.sports": "Sport",
    "events.crypto": "Cripto",
    "events.politics": "Politica",
    "events.tech": "Tech",
    "events.entertainment": "Intrattenimento",
    "events.viewAll": "Vedi tutti gli eventi",
    "events.pool": "pool",
    "events.bids": "previsioni",
    "events.bidYes": "SÌ",
    "events.bidNo": "NO",
    "events.ended": "Terminato",
    "events.remaining": "rimanente",
    "events.allCountries": "Tutti i paesi",
    "events.searchPlaceholder": "Cerca eventi...",
    "events.statusAll": "Tutti",
    "events.statusLive": "In diretta",
    "events.statusEnded": "Terminati",
    "events.statusResolved": "Risolti",
    "events.noResults": "Nessun evento trovato.",
    // How it Works
    "how.badge": "Processo semplice",
    "how.title": "Come funziona Callyout",
    "how.description": "Inizia a prevedere in pochi minuti.",
    "how.step1.title": "Crea account",
    "how.step1.description": "Registrati gratuitamente e ottieni i tuoi CallyCoins iniziali.",
    "how.step2.title": "Sfoglia eventi",
    "how.step2.description": "Esplora eventi in varie categorie.",
    "how.step3.title": "Fai previsioni",
    "how.step3.description": "Scegli SÌ o NO sui risultati degli eventi.",
    "how.step4.title": "Guadagna ricompense",
    "how.step4.description": "Vinci CallyCoins quando le tue previsioni sono corrette.",
    "how.step5.title": "Crea eventi e guadagna",
    "how.step5.description": "Crea i tuoi eventi e guadagna il 2% del pool dei perdenti.",
    // Footer
    "footer.tagline": "Il futuro delle previsioni di eventi",
    "footer.platform": "Piattaforma",
    "footer.company": "Azienda",
    "footer.legal": "Legale",
    "footer.rights": "Tutti i diritti riservati.",
    // Store
    "store.title": "Ottieni",
    "store.titleHighlight": "CallyCoins",
    "store.description": "Acquista monete per fare previsioni",
    "store.activeSubscription": "Abbonamento attivo",
    "store.renews": "Si rinnova",
    "store.manageSubscription": "Gestisci abbonamento",
    "store.coinPackages": "Pacchetti monete",
    "store.mostPopular": "Più popolare",
    "store.save": "Risparmia 20%",
    "store.bestValue": "Miglior valore!",
    "store.oneTimePurchase": "Acquisto singolo",
    "store.buyNow": "Acquista ora",
    "store.monthlySubscriptions": "Abbonamenti mensili",
    "store.recommended": "Consigliato",
    "store.yourPlan": "Il tuo piano",
    "store.perMonth": "/mese",
    "store.coinsEveryMonth": "monete ogni mese",
    "store.managePlan": "Gestisci piano",
    "store.subscribe": "Abbonati",
    "store.basicMonthly": "Base mensile",
    "store.premiumMonthly": "Premium mensile",
    "store.coinsPerMonth": "CallyCoins/mese",
    "store.standardSupport": "Supporto standard",
    "store.prioritySupport": "Supporto prioritario",
    "store.earlyAccess": "Accesso anticipato agli eventi",
    // Promo Page
    "promo.urgencyBadge": "🎁 Tempo Limitato: Bonus Iscrizione Gratuito",
    "promo.headline": "Trasforma le Previsioni in Ricompense",
    "promo.subheadline": "Unisciti a migliaia che fanno previsioni su eventi reali e guadagnano CallyCoins",
    "promo.ctaPrimary": "Riscatta i Tuoi 100 CallyCoins GRATIS",
    "promo.ctaSubtext": "Nessuna carta di credito richiesta",
    "promo.noCreditCard": "✓ Senza carta • ✓ Accesso istantaneo",
    "promo.statPredictors": "Predittori",
    "promo.statMarkets": "Mercati Live",
    "promo.statWagered": "CallyCoins Scommessi",
    "promo.benefitsTitle": "Perché Callyout?",
    "promo.benefit1Title": "Gratis per Iniziare",
    "promo.benefit1Desc": "Ottieni 100 CallyCoins istantaneamente, senza carta",
    "promo.benefit2Title": "Vinci Ricompense Reali",
    "promo.benefit2Desc": "93% del pool perdente va ai vincitori",
    "promo.benefit3Title": "Crea e Guadagna",
    "promo.benefit3Desc": "Crea eventi e guadagna il 2% di commissione creatore",
    "promo.howItWorksTitle": "Come Funziona",
    "promo.step1Title": "Scegli un Evento",
    "promo.step1Desc": "Sfoglia i mercati di previsione popolari",
    "promo.step2Title": "Fai la Tua Previsione",
    "promo.step2Desc": "Prevedi SÌ o NO sui risultati",
    "promo.step3Title": "Vinci CallyCoins",
    "promo.step3Desc": "Guadagna quando hai ragione",
    "promo.trustSecure": "Sicuro",
    "promo.trustInstant": "Istantaneo",
    "promo.trustFair": "Equo",
    "promo.trust247": "24/7",
    "promo.finalCtaTitle": "Pronto a Iniziare a Prevedere?",
    "promo.finalCtaSubtitle": "Unisciti alla nostra comunità crescente di predittori",
    "promo.finalCtaButton": "Inizia Gratis",
    "promo.terms": "Termini",
    "promo.privacy": "Privacy",
    "promo.startPredicting": "Inizia a Prevedere",
    "promo.signUpFirst": "Registrati prima",
  },
};

export const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const [country, setCountryState] = useState<Country>(() => {
    const saved = localStorage.getItem("callyout-country");
    // Fallback to 'us' if saved country is no longer valid (e.g., 'mx' was removed)
    if (saved && saved in localeConfigs) {
      return saved as Country;
    }
    return "us";
  });

  const locale = localeConfigs[country];

  useEffect(() => {
    localStorage.setItem("callyout-country", country);
  }, [country]);

  const setCountry = (newCountry: Country) => {
    setCountryState(newCountry);
  };

  const t = (key: string): string => {
    return translations[locale.language][key] || key;
  };

  const formatCurrency = (amount: number): string => {
    const localeStringMap: Record<Language, string> = {
      en: "en-US",
      pt: "pt-BR",
      es: "es-ES",
      fr: "fr-FR",
      de: "de-DE",
      it: "it-IT",
    };
    const localeString = localeStringMap[locale.language];
    
    return new Intl.NumberFormat(localeString, {
      style: "currency",
      currency: locale.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const availableCountries = Object.values(localeConfigs);

  return (
    <LocaleContext.Provider value={{ locale, setCountry, t, formatCurrency, availableCountries }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
};
